import { router, Stack, useGlobalSearchParams, useSegments } from "expo-router";
import { createContext, PropsWithChildren, useState, useEffect } from "react";
import useAuth from "@/hooks/useAuth";
import { Platform } from "react-native";
import { HeaderLeft } from "@/components/Header/HeaderLeft";
import { HeaderCenter } from "@/components/Header/HeaderCenter";
import { HeaderRight } from "@/components/Header/HeaderRight";
import { useTheme } from "@/hooks/useTheme";
import { useStorage } from "@/hooks/useStorage";
import { hasAdminPortal } from "@/lib/permissions";
import * as Device from "expo-device";

type RootContextProps = {
  setPinned: (prev: boolean) => void;
  setVisible: (prev: boolean) => void;
  pinned: boolean;
  visible: boolean;
  tabsCollapsed: boolean;
  setTabsCollapsed: (prev: boolean) => void;
};

export const RootLayoutContext = createContext<RootContextProps | undefined>(undefined);

export function RootLayoutProvider({ children }: PropsWithChildren) {
  const params = useGlobalSearchParams();
  const [pinned, setIsPinned] = useState(params.pinned === "true" ? true : false);
  const [visible, setVisible] = useState(false);

  const TAB_STORAGE_KEY = "@app_menu_width";
  const storage = useStorage();
  const [tabsCollapsed, setTabsCollapsed] = useState(storage.getItem(TAB_STORAGE_KEY) === "collapsed");

  const setPinned = (prev: boolean) => {
    if (pinned) {
      router.setParams({ pinned: undefined });
    } else {
      router.setParams({ pinned: "true" });
    }
    setIsPinned(prev);
  };

  return (
    <RootLayoutContext.Provider value={{ pinned, visible, setVisible, setPinned, setTabsCollapsed, tabsCollapsed }}>
      {children}
    </RootLayoutContext.Provider>
  );
}

export default function AppLayout() {
  const segs = useSegments();
  const { userRoles, userStatus, needsPasswordUpdate, tenantId, loading } = useAuth();
  const { colors } = useTheme();

  // Redirect to password setup if needed (runs after auth is loaded)
  const mustUpdatePassword = !loading && needsPasswordUpdate && !!tenantId;

  useEffect(() => {
    if (mustUpdatePassword) {
      router.replace("/update-password");
    }
  }, [mustUpdatePassword]);

  // Determine initial route based on user status
  const getInitialRoute = () => {
    if (userStatus === "ONBOARDING") return "sign-up";
    if (userStatus === "PENDING" || userStatus === "INACTIVE") return "account";
    return hasAdminPortal(userRoles) ? "(root)" : "(crew)";
  };

  const headerShown = () => {
    // Hide header entirely when password update is required
    if (mustUpdatePassword) return false;
    if ((segs as string[]).includes("(crew)")) return false;
    if (Platform.OS !== "web" || Device.deviceType === Device.DeviceType.PHONE) return false;
    return true;
  };
  return (
    <RootLayoutProvider>
      <Stack
        initialRouteName={getInitialRoute()}
        screenOptions={{
          headerShown: headerShown(),
          headerShadowVisible: false,
          headerTitleStyle: {
            color: colors.text,
          },
          contentStyle: {
            borderTopWidth: 1,
            borderColor: colors.border,
            backgroundColor: colors.background,
          },
          // height works at runtime but isn't in the headerStyle type definition
          headerStyle: {
            backgroundColor: colors.background,
            height: 48,
          } as any,
          title: "",
          headerLeft: () => <HeaderLeft />,
          headerTitle: () => <HeaderCenter />,
          headerRight: Platform.select({
            web: () => <HeaderRight />,
          }),
          // @ts-ignore - these props exist but aren't typed in expo-router
          headerLeftContainerStyle: {
            flex: 1,
            flexDirection: "row",
            justifyContent: "flex-start",
            alignItems: "center",
          },
          headerTitleContainerStyle: { flex: 1, flexDirection: "row" },
          headerRightContainerStyle: { flex: 1, flexDirection: "row", justifyContent: "flex-end" },
          headerBackButtonDisplayMode: "minimal",
          headerBackButtonMenuEnabled: false,
        }}
      >
        <Stack.Screen name="(root)" />
        <Stack.Screen name="(crew)" />
        <Stack.Screen name="settings" />
        <Stack.Screen name="docs" />
        <Stack.Screen name="sign-in" />
        <Stack.Screen name="privacy-policy" />
        <Stack.Screen name="terms-and-conditions" />
        <Stack.Screen name="sign-up" />
        <Stack.Screen name="new-password/[tenant]" />
        <Stack.Screen name="update-password" />
        <Stack.Screen name="account" />
        <Stack.Screen name="verify-otp" />
        <Stack.Screen name="call-request" />
      </Stack>
    </RootLayoutProvider>
  );
}
